import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../utils/AuthContext';
import { Container, Row, Col, Card, Form, Button, ProgressBar, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ShareModal from '../components/ShareModal';
import axios from 'axios';

// API URL for backend calls
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Upload = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [attendees, setAttendees] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadSource, setUploadSource] = useState('local'); // 'local', 'teams', 'zoom'
  const [showShareModal, setShowShareModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [processingComplete, setProcessingComplete] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [transcriptUrl, setTranscriptUrl] = useState(null);
  const [exportInProgress, setExportInProgress] = useState(false);
  
  const fileInputRef = useRef(null);

  // Function to handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  // Function to process selected/dropped file
  const processFile = (file) => {
    if (file) {
      // Check if file is mp4 or wav
      const fileType = file.type;
      const isVideo = fileType === 'video/mp4' || fileType.includes('video');
      const isAudio = fileType === 'audio/wav' || fileType === 'audio/mp3' || fileType === 'audio/mpeg' || fileType.includes('audio');
      
      if (!isVideo && !isAudio) {
        setErrorMessage('Please upload a video or audio file (MP4, WAV, MP3, etc.).');
        return;
      }
      
      // Check file size (max 4GB)
      if (file.size > 4 * 1024 * 1024 * 1024) {
        setErrorMessage('File size exceeds 4GB limit.');
        return;
      }
      
      setFileName(file.name);
      setFileSize(file.size);
      setUploadedFile(file);
      setErrorMessage('');
      
      // If meeting title is not set, use the file name without extension
      if (!meetingTitle) {
        const titleFromFile = file.name.split('.').slice(0, -1).join('.');
        setMeetingTitle(titleFromFile || file.name);
      }
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to trigger file browser
  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  // Function to handle file upload
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadedFile && uploadSource === 'local') {
      setErrorMessage('Please select a file to upload.');
      return;
    }
    
    setIsUploading(true);
    setErrorMessage('');
    
    try {
      // Create formData object
      const formData = new FormData();
      formData.append('recording', uploadedFile);
      
      // Upload the file
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        timeout: 120000, // 2 minutes for large files
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      if (response.data.success) {
        console.log('Upload successful:', response.data);
        setMeetingId(response.data.meetingId);
        
        // Create a meeting with additional details
        try {
          const meetingResponse = await axios.post(`${API_URL}/api/meetings`, {
            title: meetingTitle,
            meetingDate: meetingDate,
            attendees: attendees,
            userId: currentUser?.id || 'test_user' // Use the actual authenticated user ID if available
          }, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          console.log('Meeting created:', meetingResponse.data);
          
          // Move to success step
          setIsUploading(false);
          setActiveStep(2);
          
          // Start polling for meeting status
          startPollingMeetingStatus(response.data.meetingId);
        } catch (error) {
          console.error('Error creating meeting:', error);
          setErrorMessage('Error creating meeting. Please try again.');
          setIsUploading(false);
        }
      } else {
        setErrorMessage('Upload failed. Please try again.');
        setIsUploading(false);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrorMessage('Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  // Function to handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!meetingTitle || !meetingDate) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }
    setErrorMessage('');
    setActiveStep(1); // Move to upload step
  };

  // Function to view meeting details
  const viewMeeting = () => {
    if (meetingId) {
      navigate(`/summary/${meetingId}`);
    }
  };

  // Function to poll meeting status until processing is complete
  const startPollingMeetingStatus = (id) => {
    console.log('Starting to poll meeting status for meeting:', id);
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/api/meetings/${id}`);
        console.log('Poll response:', response.data);
        
        if (response.data.meeting && response.data.meeting.status === 'completed') {
          console.log('Meeting processing completed, redirecting to summary...');
          clearInterval(pollInterval);
          setProcessingComplete(true);
          // Set transcript URL if available
          if (response.data.meeting.transcriptUrl) {
            setTranscriptUrl(response.data.meeting.transcriptUrl);
          }
          // Don't automatically navigate - let user choose to view or export
          // navigate(`/summary/${id}`);
        }
      } catch (error) {
        console.error('Error polling meeting status:', error);
        clearInterval(pollInterval);
      }
    }, 3000); // Check every 3 seconds
  };

  // Function to export the meeting content
  const handleExport = async () => {
    if (!meetingId) {
      setErrorMessage('Cannot export: Meeting ID not found');
      return;
    }
    
    setExportInProgress(true);
    try {
      // Request the server to generate an export
      const response = await axios.get(`${API_URL}/api/meetings/${meetingId}/export`, {
        responseType: 'blob'  // Important for handling file downloads
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${meetingTitle || 'meeting'}_export.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting meeting:', error);
      setErrorMessage('Failed to export meeting content. Please try again.');
    } finally {
      setExportInProgress(false);
    }
  };

  // Function to return to dashboard and update analytics
  const returnToDashboardWithAnalytics = async () => {
    try {
      // Update analytics data before navigating
      await axios.post(`${API_URL}/api/analytics/update`, {
        meetingId: meetingId,
        action: 'meeting_completed'
      });
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating analytics:', error);
      // Still navigate to dashboard even if analytics update fails
      navigate('/dashboard');
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const steps = [
    {
      title: 'Meeting Details',
      content: (
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="text-white">Meeting Title*</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Enter meeting title" 
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.target.value)}
              required
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label className="text-white">Meeting Date*</Form.Label>
            <Form.Control 
              type="date" 
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              required
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label className="text-white">Attendees (Optional)</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              placeholder="Enter comma-separated list of attendees"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              className="bg-dark text-white border-secondary"
            />
            <Form.Text className="text-white">
              This helps our AI identify speakers in the transcription
            </Form.Text>
          </Form.Group>
          
          <div className="d-grid mt-4">
            <Button type="submit" variant="primary" size="lg">
              Continue to Upload
            </Button>
          </div>
        </Form>
      )
    },
    {
      title: 'Upload Recording',
      content: (
        <div>
          <div className="mb-4">
            <div className="d-flex justify-content-center mb-4">
              <div className="btn-group w-100">
                <Button 
                  variant={uploadSource === 'local' ? 'primary' : 'outline-primary'} 
                  onClick={() => setUploadSource('local')}
                  className="py-2"
                >
                  <i className="bi bi-laptop me-2"></i>
                  From Computer
                </Button>
                <Button 
                  variant={uploadSource === 'teams' ? 'primary' : 'outline-primary'} 
                  onClick={() => setUploadSource('teams')}
                  className="py-2"
                >
                  <i className="bi bi-microsoft-teams me-2"></i>
                  Microsoft Teams
                </Button>
                <Button 
                  variant={uploadSource === 'zoom' ? 'primary' : 'outline-primary'} 
                  onClick={() => setUploadSource('zoom')}
                  className="py-2"
                >
                  <i className="bi bi-camera-video me-2"></i>
                  Zoom
                </Button>
              </div>
            </div>

            {uploadSource === 'local' && (
              <div
                className={`drop-area text-center p-5 mb-4 rounded ${isDragging ? 'dragging' : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFileDialog}
                style={{
                  border: `2px dashed ${isDragging ? '#007bff' : 'rgba(255, 255, 255, 0.2)'}`,
                  backgroundColor: isDragging ? 'rgba(0, 123, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <input 
                  type="file" 
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="video/*,audio/*"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                
                <div className="mb-3 text-primary" style={{ fontSize: '3rem' }}>
                  <i className={isDragging ? "bi bi-cloud-arrow-down-fill" : "bi bi-cloud-upload"}></i>
                </div>
                
                <h5 className="text-white mb-3">
                  {isDragging ? 'Drop your file here' : 'Drag & drop your meeting recording here'}
                </h5>
                
                <p className="text-white mb-4">
                  or <span className="text-primary">click to browse</span> your files
                </p>
                
                <div className="d-flex justify-content-center">
                  <Badge bg="dark" className="me-2 text-white">
                    MP4
                  </Badge>
                  <Badge bg="dark" className="me-2 text-white">
                    WAV
                  </Badge>
                  <Badge bg="dark" className="me-2 text-white">
                    MP3
                  </Badge>
                </div>
                
                <p className="text-white mt-3">
                  <small>Maximum file size: 4GB</small>
                </p>
              </div>
            )}

            {uploadSource === 'teams' && (
              <Card className="glass-card mb-4">
                <Card.Body className="text-center p-5">
                  <div className="mb-3 text-primary" style={{ fontSize: '3rem' }}>
                    <i className="bi bi-microsoft-teams"></i>
                  </div>
                  <h5 className="text-white mb-3">Connect to Microsoft Teams</h5>
                  <p className="text-white mb-4">
                    Select a meeting recording directly from your Microsoft Teams account
                  </p>
                  <Button variant="outline-primary">
                    <i className="bi bi-microsoft me-2"></i>
                    Connect Microsoft Account
                  </Button>
                </Card.Body>
              </Card>
            )}

            {uploadSource === 'zoom' && (
              <Card className="glass-card mb-4">
                <Card.Body className="text-center p-5">
                  <div className="mb-3 text-primary" style={{ fontSize: '3rem' }}>
                    <i className="bi bi-camera-video"></i>
                  </div>
                  <h5 className="text-white mb-3">Connect to Zoom</h5>
                  <p className="text-white mb-4">
                    Select a meeting recording directly from your Zoom account
                  </p>
                  <Button variant="outline-primary">
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Connect Zoom Account
                  </Button>
                </Card.Body>
              </Card>
            )}
          </div>
          
          {fileName && uploadSource === 'local' && (
            <Card className="mb-4 glass-card">
              <Card.Body>
                <h6 className="text-white">File Details:</h6>
                <div className="d-flex align-items-center">
                  <div className="me-3 text-primary" style={{ fontSize: '2rem' }}>
                    <i className={fileName.toLowerCase().endsWith('.mp4') ? "bi bi-file-earmark-play" : "bi bi-file-earmark-music"}></i>
                  </div>
                  <div>
                    <p className="mb-1 text-white">{fileName}</p>
                    <p className="mb-0 text-white">{formatFileSize(fileSize)}</p>
                  </div>
                  <Button 
                    variant="link" 
                    className="ms-auto text-danger"
                    onClick={() => {
                      setFileName('');
                      setFileSize(0);
                    }}
                  >
                    <i className="bi bi-x-circle"></i>
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
          
          {uploadProgress > 0 && (
            <div className="mb-4">
              <ProgressBar 
                now={uploadProgress} 
                label={`${uploadProgress}%`} 
                animated={isUploading}
              />
              <p className="text-center mt-2 small text-white">
                {isUploading ? 'Uploading...' : 'Upload complete!'}
              </p>
            </div>
          )}
          
          <div className="d-flex justify-content-between">
            <Button 
              variant="outline-secondary" 
              onClick={() => setActiveStep(0)}
              disabled={isUploading}
            >
              Back
            </Button>
            <Button 
              variant="primary" 
              onClick={handleUpload}
              disabled={(uploadSource === 'local' && !fileName) || isUploading || uploadProgress === 100}
            >
              {isUploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </div>
        </div>
      )
    },
    {
      title: 'Processing',
      content: (
        <div className="text-center">
          <div className="mb-4">
            <div className="bg-success bg-opacity-10 text-success p-4 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '100px', height: '100px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="bi bi-check-lg" viewBox="0 0 16 16">
                <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/>
              </svg>
            </div>
            <h4 className="text-white mb-2">Upload Successful!</h4>
            <p className="text-white mb-4">
              {processingComplete 
                ? 'Your recording has been processed and is ready!' 
                : 'Your recording is now being processed. This usually takes a few minutes.'}
            </p>
          </div>
          
          <Card className="glass-card mb-4">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <i className={`bi ${processingComplete ? 'bi-check-circle-fill text-success' : 'bi-clock-history text-primary'}`} style={{ fontSize: '2rem' }}></i>
                </div>
                <div className="text-start">
                  <h6 className="mb-1 text-white">
                    {processingComplete ? 'Processing complete!' : 'Processing your recording'}
                  </h6>
                  <p className="mb-2 text-white small">
                    {processingComplete 
                      ? 'Your meeting has been fully processed and is ready to view or export' 
                      : 'This may take several minutes depending on the length of your recording'}
                  </p>
                  {!processingComplete && (
                    <ProgressBar now={45} animated className="mt-2" style={{ height: '8px' }} />
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
          
          <div className="d-flex justify-content-between">
            <Button 
              variant="outline-secondary" 
              onClick={returnToDashboardWithAnalytics}
            >
              {processingComplete ? 'Done' : 'Return to Dashboard'}
            </Button>
            <div>
              {processingComplete && (
                <Button 
                  variant="outline-primary" 
                  className="me-2" 
                  onClick={handleExport} 
                  disabled={exportInProgress}
                >
                  <i className="bi bi-download me-2"></i>
                  {exportInProgress ? 'Exporting...' : 'Export'}
                </Button>
              )}
              <Button 
                variant="outline-primary" 
                className="me-2" 
                onClick={() => setShowShareModal(true)}
                disabled={!processingComplete}
              >
                <i className="bi bi-share me-2"></i>Share
              </Button>
              <Button 
                variant="primary" 
                onClick={viewMeeting}
                disabled={!processingComplete}
              >
                View Meeting
              </Button>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Summary',
      content: (
        <div className="text-center">
          <Alert variant="success" className="mb-4">
            <h4 className="alert-heading">Meeting Summary</h4>
            <p>
              Your meeting summary is ready! You can view the summary, download the transcript, and share the results with others.
            </p>
          </Alert>
          
          <div className="text-center mt-5">
            <h4 className="text-white mb-4">Your meeting is ready!</h4>
            <p className="text-white">
              We've processed your recording and generated insights.
            </p>
            
            <div className="d-grid gap-3 mt-4">
              <Button variant="primary" size="lg" onClick={viewMeeting}>
                View Meeting Details
              </Button>
              <Button variant="outline-light" onClick={() => setShowShareModal(true)}>
                <i className="bi bi-share me-2"></i>
                Share Results
              </Button>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <Container fluid className="p-3 p-md-4">
      <Row className="mb-4">
        <Col>
          <h1 className="text-white">Meeting Details</h1>
          <p className="text-white">Upload your Microsoft Teams meeting recordings for transcription and summary</p>
        </Col>
      </Row>
      
      {errorMessage && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}
      
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between mb-4">
            {steps.map((step, index) => (
              <div key={index} className="text-center" style={{ flex: 1 }}>
                <div 
                  className={`
                    rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2
                    ${index < activeStep ? 'bg-success' : (index === activeStep ? 'bg-primary' : 'bg-dark')}
                  `}
                  style={{ width: '36px', height: '36px' }}
                >
                  {index < activeStep ? (
                    <i className="bi bi-check text-white"></i>
                  ) : (
                    <span className="text-white">{index + 1}</span>
                  )}
                </div>
                <div className={index === activeStep ? 'text-white' : 'text-white'}>
                  {step.title}
                </div>
              </div>
            ))}
          </div>
        </Col>
      </Row>
      
      <Row>
        <Col>
          <Card className="glass-card">
            <Card.Body className="p-4">
              <h3 className="mb-4 text-white">{steps[activeStep].title}</h3>
              {steps[activeStep].content}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Share Modal */}
      <ShareModal 
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        meetingData={{
          title: meetingTitle || fileName || 'Uploaded Meeting',
          date: meetingDate || new Date().toLocaleDateString()
        }}
        shareType="all"
      />
    </Container>
  );
};

export default Upload;
